import { ButtonKind, ButtonType } from "@spscommerce/ds-shared";
import clsx from "clsx";
import * as React from "react";

import { SpsButton } from "../button/SpsButton";
import {
    SpsFormArrayMeta,
    SpsFormGroupMeta,
} from "../form/hooks/useSpsForm";
import { SpsFormArray } from "../form/hooks/formArray";
import { SpsFormGroup } from "../form/hooks/formGroup";
import { SpsForm, spsFormProps, spsFormPropTypes } from "../form/SpsForm";
import { I18nContext } from "../i18n";
import * as PropTypes from "../prop-types";
import { contentOf } from "../utils/contentOf";

const propsDoc = {
    ...spsFormProps,
    controlsDisabled: "boolean",
    footerLinks: "ReactNodeOrRenderFn",
    formMeta: "SpsFormArrayMeta<any> | SpsFormGroupMeta<any>",
    isOpen: "boolean",
    onClear: "() => void",
    onSubmit: "React.FormEventHandler",
    pinned: { type: "boolean", deprecated: true },
};

const propTypes = {
    ...PropTypes.spsGlobalPropTypes,
    ...spsFormPropTypes,
    controlsDisabled: PropTypes.bool,
    footerLinks: PropTypes.nodeOrRenderFn,
    formArray: PropTypes.impl<SpsFormArray<any>>(),
    formGroup: PropTypes.impl<SpsFormGroup>(),
    formMeta: PropTypes.oneOfType([
        PropTypes.impl<SpsFormArrayMeta<any>>(),
        PropTypes.impl<SpsFormGroupMeta<any>>(),
    ]),
    isOpen: PropTypes.bool,
    onClear: PropTypes.fun<() => void>(),
    onSubmit: PropTypes.fun<React.FormEventHandler>(),
    pinned: PropTypes.bool,
};

export type SpsAdvancedSearchProps = PropTypes.InferTS<typeof propTypes, HTMLDivElement>;

export function SpsAdvancedSearch(props: SpsAdvancedSearchProps) {
    const {
        children,
        className,
        controlsDisabled,
        footerLinks,
        formArray,
        formGroup,
        formMeta,
        isOpen,
        pinned,
        onClear,
        onSubmit,
        unsafelyReplaceClassName,
        ...rest
    } = props;

    if (typeof pinned !== "undefined") {
        console.warn(`
            SpsAdvancedSearch: The 'pinned' prop is deprecated.
            The advanced search component will no longer pin, and this prop will be removed in a future release.
            Please remove it from your usage of the component.
        `.replace(/\w{2,}/g, " ").trim());
    }

    const { t } = React.useContext(I18nContext);

    function handleClear() {
        if (onClear) {
            onClear();
        }
    }

    const toolbarClass = clsx(
        unsafelyReplaceClassName || "sps-advanced-search",
        isOpen && "sps-advanced-search--open",
        className,
    );

    return (
        <div className={toolbarClass} {...rest}>
            <SpsForm onSubmit={onSubmit as React.FormEventHandler}
                formArray={formArray}
                formGroup={formGroup}
                formMeta={formMeta}
            >
                <div className="sps-advanced-search__form">{children}</div>
                <div className="sps-advanced-search__footer">
                    {footerLinks && <div id="footer_links">{contentOf(footerLinks)}</div>}
                    <div className="sps-advanced-search__footer-controls">
                        <SpsButton
                            kind={ButtonKind.DEFAULT}
                            type={ButtonType.BUTTON}
                            className="mr-2"
                            onClick={handleClear}
                            disabled={controlsDisabled}
                        >
                            {t("design-system:advancedSearch.clear")}
                        </SpsButton>
                        <SpsButton
                            kind={ButtonKind.KEY}
                            type={ButtonType.SUBMIT}
                            disabled={controlsDisabled}
                        >
                            {t("design-system:advancedSearch.search")}
                        </SpsButton>
                    </div>
                </div>
            </SpsForm>
        </div>
    );
}

Object.assign(SpsAdvancedSearch, {
    props: propsDoc,
    propTypes,
    displayName: "SpsAdvancedSearch"
});
